import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import PageHeading from '../../components/common/PageHeading';
import { useNudgePrefs } from '../../hooks/useNudgePrefs';
import { useAuth } from '../../context/AuthContext';
import { updateMemberPreferences, getMyActivityPreferences, updateMyActivityPreference } from '../../services/hubEvents.service';
import * as membershipService from '../../services/membership.service';
import api from '../../services/api';
import { uploadImage } from '../../services/upload.service';
import SummaryShareSettings from '../../components/dashboard/SummaryShareSettings';

const DASHBOARD_REMINDER_TIMES = [
  { field: 'dashboard_reminder_7am_et',  label: '7am ET' },
  { field: 'dashboard_reminder_9am_et',  label: '9am ET' },
  { field: 'dashboard_reminder_noon_et', label: 'Noon ET' },
  { field: 'dashboard_reminder_5pm_et',  label: '5pm ET' },
  { field: 'dashboard_reminder_9pm_et',  label: '9pm ET' },
];

/**
 * Member Settings page — Nudge preferences only.
 * Comfort zone sliders removed — continuums are set in My Continuums using your own words.
 */
export default function Settings() {
  const { user } = useAuth();
  // While paused the Hub is only this page, and this page is only the
  // pause, password and cancel controls (Jenn, 2026-10-07).
  const paused = !!user?.pause_ends_at && new Date(user.pause_ends_at) > new Date();
  if (paused) {
    return (
      <DashboardLayout>
        <div className="mb-6">
          <PageHeading title="Settings" subtitle="Your membership is paused." />
        </div>
        <div className="space-y-8">
          <MembershipSettings />
          <PasswordSettings />
        </div>
      </DashboardLayout>
    );
  }
  return (
    <DashboardLayout>
      <div className="mb-6">
        <PageHeading title="Settings" subtitle="Adjust how 365reset reaches out to you." />
      </div>

      <div className="space-y-8">
        <SummaryShareSettings />
        <ProfileSettings />
        <MembershipSettings />
        <SequenceSettings />
        <PasswordSettings />
        <DailyContentSettings />
        <DashboardSettings />
        <PrivacySettings />
        <NudgeSettings />
      </div>
    </DashboardLayout>
  );
}

// ─── Profile Settings ─────────────────────────────────────────────────────────

// Square-crops the middle of the picture and shrinks it to 480px, so a 5MB
// phone photo uploads as a small JPEG (Jenn, 2026-10-28: members upload their
// own photo instead of pasting a link).
const squarePhoto = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 480;
    canvas.getContext('2d').drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, 480, 480);
    URL.revokeObjectURL(url);
    canvas.toBlob(b => (b ? resolve(new File([b], 'photo.jpg', { type: 'image/jpeg' })) : reject(new Error('Could not read that picture.'))), 'image/jpeg', 0.88);
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that picture. Please try a different one.')); };
  img.src = url;
});

function ProfileSettings() {
  const { user, updateUser } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const fileRef = React.useRef(null);

  const save = async (url) => {
    const { data } = await api.put('/members/me', { avatar_url: url });
    updateUser({ avatar_url: data.avatar_url });
    setAvatarUrl(data.avatar_url || '');
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSaveErr(null); setSaved(false); setSaving(true);
    try {
      await save(await uploadImage(await squarePhoto(file)));
    } catch (err) {
      setSaveErr(err.response?.data?.error || err.message || 'Could not upload your photo. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaveErr(null); setSaving(true);
    try { await save(null); } catch (err) { setSaveErr('Could not remove your photo. Please try again.'); } finally { setSaving(false); }
  };

  return (
    <SettingsSection title="Your Photo" subtitle="Shown next to your posts and comments, and to the 365reset team.">
      <div className="flex items-center gap-4 flex-wrap">
        {avatarUrl ? (
          <img src={avatarUrl} alt={`${user?.name || 'Your'} photo`} className="w-20 h-20 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-reset-sage text-white flex items-center justify-center text-2xl font-bold shrink-0" aria-hidden="true">
            {(user?.name || '?').trim().charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex flex-col gap-2">
          <input ref={fileRef} type="file" accept="image/*" onChange={handlePick} className="hidden" aria-hidden="true" tabIndex={-1} />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={saving}
            className="px-4 py-2.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : saved ? '✓ Saved' : avatarUrl ? 'Change photo' : 'Upload a photo'}
          </button>
          {avatarUrl && !saving && (
            <button type="button" onClick={handleRemove} className="text-xs text-gray-500 hover:text-reset-brown text-left">Remove photo</button>
          )}
        </div>
      </div>
      {saveErr && <p className="text-xs text-red-600 mt-3">{saveErr}</p>}
    </SettingsSection>
  );
}

// ─── Membership ─────────────────────────────────────────────────────────────
//
// Cancel/renew here are real, immediate actions against Stripe — not a
// request sent to Team365 for review. A reason is required before either
// cancel path submits, purely so Team365 gets real feedback to act on; it
// doesn't gate or delay the cancellation itself, and there's no retention
// offer or discount pitch here on purpose — matches the standing "no
// trigger/bump marketing, ever" rule.

function MembershipSettings() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(null); // null | 'at_renewal' | 'now'
  const [reason, setReason] = useState('');
  const [reasonChoice, setReasonChoice] = useState(''); // optional main reason (2026-09-29)
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // last action's outcome, for a confirmation message

  const load = () => {
    membershipService.getMembershipSummary()
      .then(setData)
      .catch(err => setError(err.response?.data?.error || 'Could not load your membership.'));
  };
  useEffect(() => { load(); }, []);

  const openConfirm = (which) => { setConfirming(which); setReason(''); setReasonChoice(''); setError(null); };
  const closeConfirm = () => { setConfirming(null); setReason(''); setReasonChoice(''); };

  const submitCancel = async () => {
    if (!reason.trim()) { setError('Please share a reason before cancelling.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      if (confirming === 'at_renewal') {
        await membershipService.cancelAtRenewal(reason.trim(), reasonChoice);
        setResult('Your membership is set to end at your next renewal date. We\'ve sent you an email — thank you for telling us why.');
      } else {
        await membershipService.cancelNow(reason.trim(), reasonChoice);
        setResult('Your membership has been cancelled. We\'ve sent you an email — thank you for telling us why.');
      }
      closeConfirm();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenewNow = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await membershipService.renewNow();
      setResult('Your membership will keep renewing as normal.');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not resume your membership.');
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !data) return <SettingsSection title="Membership"><ErrorMsg msg={error} /></SettingsSection>;
  if (!data) return <SettingsSection title="Membership"><Skeleton /></SettingsSection>;

  const { joined_at, is_active, payment, founder_pricing, product_name, membership_type, membership_expires_at,
          term_ends_at, auto_renew, pause } = data;
  const isFreeArrangement = membership_type === 'trial' || membership_type === 'free_annual' || membership_type === 'royalty';

  return (
    <SettingsSection title="Membership" subtitle="Your plan, renewal, and account status.">
      <div className="flex items-center gap-2 flex-wrap">
        {founder_pricing && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-reset-gold/40 border border-reset-gold rounded-full text-xs font-semibold text-reset-brown">
            ★ Founding Member
          </div>
        )}
        {isFreeArrangement && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-reset-sage/10 border border-reset-sage rounded-full text-xs font-semibold text-reset-sage">
            {membership_type === 'trial' ? 'Free trial' : membership_type === 'royalty' ? 'Royalty member (free)' : 'Free 1-year membership'}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div>
          <p className="text-gray-500">Plan</p>
          <p className="text-reset-brown font-medium">{product_name || '365reset'}</p>
        </div>
        <div>
          <p className="text-gray-500">Joined</p>
          <p className="text-reset-brown font-medium">{new Date(joined_at).toLocaleDateString()}</p>
        </div>
        <div>
          <p className="text-gray-500">Status</p>
          <p className={`font-medium ${is_active ? 'text-green-600' : 'text-gray-400'}`}>
            {is_active ? 'Active' : 'Inactive'}
          </p>
        </div>
        {isFreeArrangement && membership_expires_at && (
          <div>
            <p className="text-gray-500">{membership_type === 'trial' ? 'Trial ends' : 'Free membership ends'}</p>
            <p className="text-reset-brown font-medium">{new Date(membership_expires_at).toLocaleDateString()}</p>
          </div>
        )}
        {founder_pricing && (
          <div>
            <p className="text-gray-500">Founder rate</p>
            <p className="text-reset-brown font-medium">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: (founder_pricing.currency || 'usd').toUpperCase() }).format(founder_pricing.amount)}
              {founder_pricing.interval ? ` / ${founder_pricing.interval}` : ''}
            </p>
          </div>
        )}
        {founder_pricing?.expires_at && (
          <div>
            <p className="text-gray-500">Founder rate through</p>
            <p className="text-reset-brown font-medium">{new Date(founder_pricing.expires_at).toLocaleDateString()}</p>
          </div>
        )}
        {payment?.amount != null && (
          <div>
            <p className="text-gray-500">Next payment</p>
            <p className="text-reset-brown font-medium">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: (payment.currency || 'usd').toUpperCase() }).format(payment.amount)}
              {payment.interval ? ` / ${payment.interval}` : ''}
            </p>
          </div>
        )}
        {term_ends_at && (
          <div>
            <p className="text-gray-500">{auto_renew ? 'Membership year renews' : 'Membership year ends'}</p>
            <p className="text-reset-brown font-medium">{new Date(term_ends_at).toLocaleDateString()}</p>
          </div>
        )}
        {payment?.currentPeriodEnd && (
          <div>
            <p className="text-gray-500">{payment.cancelAtPeriodEnd ? 'Ends on' : 'Next payment on'}</p>
            <p className="text-reset-brown font-medium">{new Date(payment.currentPeriodEnd).toLocaleDateString()}</p>
          </div>
        )}
      </div>

      {result && <p className="text-sm text-reset-sage bg-reset-cream rounded-lg px-3 py-2">{result}</p>}
      {error && confirming === null && <ErrorMsg msg={error} />}

      {is_active && term_ends_at && !pause && (
        <AutoRenewSetting on={auto_renew} termEndsAt={term_ends_at} onSaved={load} />
      )}
      {is_active && (
        <PaymentDetails data={data} onChanged={(msg) => { setResult(msg); load(); }} />
      )}
      {is_active && <PauseSetting pause={pause} onChanged={(msg) => { setResult(msg); load(); }} />}

      {!payment ? (
        <p className="text-sm text-gray-400">No active membership on file.</p>
      ) : confirming ? (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-reset-brown">
            {confirming === 'now'
              ? 'This ends your access right away. Before you go — what led to this?'
              : 'Your access continues until your renewal date. Before you go — what led to this?'}
          </p>
          {/* Optional main reason (2026-09-29) — helps Team365 see patterns; the words below stay the heart of it. */}
          <select
            value={reasonChoice} onChange={e => setReasonChoice(e.target.value)}
            aria-label="Main reason (optional)"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          >
            <option value="">Main reason (optional)</option>
            <option value="price">The price</option>
            <option value="no_time">I don't have time right now</option>
            <option value="not_using">I'm not using it enough</option>
            <option value="life_change">Something changed in my life</option>
            <option value="other">Something else</option>
          </select>
          <textarea
            value={reason} onChange={e => setReason(e.target.value)} rows={3}
            placeholder="Your honest feedback helps us do better for other members."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          {error && <ErrorMsg msg={error} />}
          <div className="flex justify-end gap-2">
            <button onClick={closeConfirm} className="px-4 py-2 text-sm text-gray-500 hover:text-reset-brown">
              Never mind
            </button>
            <button
              onClick={submitCancel} disabled={submitting}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : confirming === 'now' ? 'Cancel now' : 'Cancel at renewal'}
            </button>
          </div>
        </div>
      ) : payment.cancelAtPeriodEnd ? (
        <button
          onClick={handleRenewNow} disabled={submitting}
          className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
        >
          {submitting ? 'Working…' : 'Renew now'}
        </button>
      ) : (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => openConfirm('at_renewal')} className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:border-reset-terracotta hover:text-reset-terracotta transition-colors">
            Cancel at renewal
          </button>
          <button onClick={() => openConfirm('now')} className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:border-reset-terracotta hover:text-reset-terracotta transition-colors">
            Cancel now
          </button>
        </div>
      )}
    </SettingsSection>
  );
}

// ─── Yearly auto-renew (2026-10-07) ───────────────────────────────────────
// Members pay monthly inside a one-year term. On: a new year starts on its
// own. Off: the membership ends on that day. We email 30 days before either way.

function AutoRenewSetting({ on, termEndsAt, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const when = new Date(termEndsAt).toLocaleDateString();

  const change = async (next) => {
    setSaving(true); setErr(null);
    try { await membershipService.setAutoRenew(next); onSaved(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not save that change.'); }
    finally { setSaving(false); }
  };

  return (
    <div className={saving ? 'opacity-60 pointer-events-none' : ''}>
      <Toggle
        label="Auto-renew my membership each year"
        description={on
          ? `A new year starts on ${when}. We'll email you before then.`
          : `Your membership ends on ${when}. We'll email you before then.`}
        checked={!!on}
        onChange={change}
      />
      {err && <ErrorMsg msg={err} />}
    </div>
  );
}

// ─── Payment details (2026-11-03) ────────────────────────────────────────
// The card Stripe will charge, a button to Stripe's secure page to see
// payments and add or change the card, and — for Founding Members — how
// they pay their renewal: CA$49.95 for the year or CA$4.16 a month.
// Royalty recipients see that their renewal is covered by 365reset.

const cad = (n) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(n);

function PaymentDetails({ data, onChanged }) {
  const { card, royalty, founder_plan: plan, payment, auto_renew } = data;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (royalty) {
    return (
      <div className="border border-gray-200 rounded-xl p-4">
        <p className="text-sm font-medium text-reset-brown">Payment details</p>
        <p className="text-sm text-gray-600 mt-1">As a royalty recipient, your renewal cost is covered by 365reset.</p>
      </div>
    );
  }
  if (!payment) return null;

  const openCardPage = async (cardOnly) => {
    setBusy(true); setErr(null);
    try { const { url } = await membershipService.openCardPage(cardOnly); window.location.href = url; }
    catch (e) { setErr(e.response?.data?.error || 'Could not open your payment details.'); setBusy(false); }
  };

  const choose = async (interval) => {
    setBusy(true); setErr(null);
    try {
      const r = await membershipService.chooseFounderPlan(interval);
      if (r.needsCard) { await openCardPage(true); return; }
      onChanged(interval === 'month'
        ? `You'll renew at ${cad(plan.monthly)} a month. Thank you for staying with us!`
        : `You'll renew at ${cad(plan.yearly)} for the whole year. Thank you for staying with us!`);
    } catch (e) { setErr(e.response?.data?.error || 'Could not save that change.'); }
    finally { setBusy(false); }
  };

  const brand = card?.brand ? card.brand[0].toUpperCase() + card.brand.slice(1) : 'Card';
  const chosen = auto_renew ? payment.interval : null;
  const btn = 'px-4 py-2 text-sm font-medium rounded-xl transition-colors disabled:opacity-50';

  return (
    <div className={`border border-gray-200 rounded-xl p-4 space-y-3 ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
      <p className="text-sm font-medium text-reset-brown">Payment details</p>
      <p className="text-sm text-gray-600">
        {!card ? 'There is no card on file yet.'
          : card.expired ? `Your ${brand}${card.last4 ? ` ending ${card.last4}` : ''} has expired.`
          : `Your payments go on your ${brand}${card.last4 ? ` ending ${card.last4}` : ''}${card.expMonth ? ` (expires ${card.expMonth}/${card.expYear})` : ''}.`}
      </p>
      {plan && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            As a Founding Member, your renewal is {cad(plan.yearly)} for the whole year, or {cad(plan.monthly)} a month.
            {chosen ? ` You've chosen ${chosen === 'month' ? 'monthly' : 'the whole year'}.` : ' Choose how you\'d like to pay:'}
          </p>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => choose('year')} disabled={chosen === 'year'}
              className={`${btn} ${chosen === 'year' ? 'bg-reset-sage text-white' : 'bg-reset-terracotta text-white hover:bg-reset-brown'}`}>
              {chosen === 'year' ? '✓ ' : ''}Renew for the year: {cad(plan.yearly)}
            </button>
            <button onClick={() => choose('month')} disabled={chosen === 'month'}
              className={`${btn} ${chosen === 'month' ? 'bg-reset-sage text-white' : 'bg-reset-terracotta text-white hover:bg-reset-brown'}`}>
              {chosen === 'month' ? '✓ ' : ''}Renew monthly: {cad(plan.monthly)} a month
            </button>
          </div>
        </div>
      )}
      <button onClick={() => openCardPage(!card || card.expired)}
        className={`${btn} border border-gray-200 text-gray-600 hover:border-reset-terracotta hover:text-reset-terracotta`}>
        {!card || card.expired ? 'Add my card' : 'See payments or update my card'}
      </button>
      {err && <ErrorMsg msg={err} />}
    </div>
  );
}

// ─── Pause (2026-10-07) ──────────────────────────────────────────────────
// 3 or 6 months. No charges while paused; the next payment and the end of
// the membership year both move back by the length of the pause. While
// paused the Hub shows only Settings; it opens again on its own at the end,
// or straight away with "End my pause now".

const PAUSE_REASON_LABELS = { travelling: 'Travelling', health: 'Health reason', family: 'Family matter' };

function PauseSetting({ pause, onChanged }) {
  const { updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState(3);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const start = async () => {
    if (!reason) { setErr('Please choose a reason.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await membershipService.pauseMembership(months, reason);
      setOpen(false);
      onChanged(`Your membership is paused until ${new Date(r.pauseEndsAt).toLocaleDateString()}. We've sent you an email.`);
      updateUser({ pause_ends_at: r.pauseEndsAt });
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not pause your membership. Please try again.');
    } finally { setBusy(false); }
  };

  const end = async () => {
    setBusy(true); setErr(null);
    try {
      await membershipService.endPause();
      onChanged('Welcome back! Your full Hub is open again.');
      updateUser({ pause_ends_at: null });
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not end your pause. Please try again.');
    } finally { setBusy(false); }
  };

  if (pause) {
    return (
      <div className="border border-reset-sage/40 bg-reset-cream rounded-xl p-4 space-y-3">
        <p className="text-sm text-reset-brown">
          <span className="font-medium">Paused until {new Date(pause.ends_at).toLocaleDateString()}</span>
          {PAUSE_REASON_LABELS[pause.reason] ? ` · ${PAUSE_REASON_LABELS[pause.reason]}` : ''}
        </p>
        <p className="text-xs text-gray-500">
          No payments while you're paused. Everything opens again on its own when your pause ends.
        </p>
        {err && <ErrorMsg msg={err} />}
        <button onClick={end} disabled={busy}
          className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
          {busy ? 'Working…' : 'End my pause now'}
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div>
        <button onClick={() => { setOpen(true); setErr(null); }}
          className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:border-reset-terracotta hover:text-reset-terracotta transition-colors">
          Pause my membership
        </button>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
      <p className="text-sm font-medium text-reset-brown">Pause your membership</p>
      <p className="text-xs text-gray-500">
        No payments while you're paused, and your next payment and membership year both move back by the same
        amount of time, so you don't lose anything. While paused you'll only see this Settings page.
      </p>
      <div className="flex gap-2">
        {[3, 6].map(m => (
          <button key={m} type="button" onClick={() => setMonths(m)} aria-pressed={months === m}
            className={`px-4 py-2 text-sm rounded-xl border transition-colors ${months === m
              ? 'bg-reset-terracotta border-reset-terracotta text-white' : 'border-gray-200 text-gray-600 hover:border-reset-terracotta'}`}>
            {m} months
          </button>
        ))}
      </div>
      <select value={reason} onChange={e => setReason(e.target.value)} aria-label="Reason for pausing"
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta">
        <option value="">Reason for pausing</option>
        {Object.entries(PAUSE_REASON_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {err && <ErrorMsg msg={err} />}
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-reset-brown">Never mind</button>
        <button onClick={start} disabled={busy}
          className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
          {busy ? 'Pausing…' : `Pause for ${months} months`}
        </button>
      </div>
    </div>
  );
}

// ─── Sequence Settings ────────────────────────────────────────────────────────
// Shows where the member is in the daily content sequence and lets them
// restart it from Day 1 if they want to. Onboarding/renewal sequences aren't
// surfaced here — daily is the one that matters to a member day-to-day.

function SequenceSettings() {
  const [state, setState] = useState(null);
  const [dailyEmailsEnabled, setDailyEmailsEnabled] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [savingEmailToggle, setSavingEmailToggle] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    membershipService.getMySequenceState()
      .then(d => { setState(d.sequence_state); setDailyEmailsEnabled(d.daily_content_enabled); })
      .catch(err => setError(err.response?.data?.error || 'Could not load your progress.'));
  };
  useEffect(() => { load(); }, []);

  const handleToggleDailyEmails = async (next) => {
    setSavingEmailToggle(true);
    setError(null);
    try {
      await membershipService.setMyDailyContentEnabled(next);
      setDailyEmailsEnabled(next);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this.');
    } finally {
      setSavingEmailToggle(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setError(null);
    try {
      await membershipService.resetMySequence('daily');
      setResult('You\'re back at Day 1 — your next daily content will start from the beginning.');
      setConfirming(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reset your progress.');
    } finally {
      setResetting(false);
    }
  };

  if (error && !state) return <SettingsSection title="Daily Sequence"><ErrorMsg msg={error} /></SettingsSection>;
  if (!state) return <SettingsSection title="Daily Sequence"><Skeleton /></SettingsSection>;

  const daily = state.find(s => s.sequence_type === 'daily');

  return (
    <SettingsSection title="Daily Sequence" subtitle="Where you are in your daily content.">
      <div className="text-sm">
        <p className="text-gray-500">Current day</p>
        <p className="text-reset-brown font-medium">Day {daily?.current_day ?? 1}</p>
      </div>

      <Toggle
        label="Daily emails"
        description={dailyEmailsEnabled
          ? "You're receiving daily content emails. You'll still get membership and account emails either way."
          : "Daily content emails are off. You'll still receive membership and key account emails."}
        checked={!!dailyEmailsEnabled}
        onChange={handleToggleDailyEmails}
      />

      {result && <p className="text-sm text-reset-sage bg-reset-cream rounded-lg px-3 py-2">{result}</p>}
      {error && !confirming && <ErrorMsg msg={error} />}

      {confirming ? (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-reset-brown">
            This starts your daily content over from Day 1. Are you sure?
          </p>
          {error && <ErrorMsg msg={error} />}
          <div className="flex justify-end gap-2">
            <button onClick={() => setConfirming(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-reset-brown">
              Never mind
            </button>
            <button
              onClick={handleReset} disabled={resetting}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
            >
              {resetting ? 'Resetting…' : 'Start over at Day 1'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => { setConfirming(true); setError(null); }}
          className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
        >
          Start from Day 1 again
        </button>
      )}
    </SettingsSection>
  );
}

// ─── Password Settings ────────────────────────────────────────────────────────

function PasswordSettings() {
  const [form, setForm]       = useState({ password: '', confirm: '' });
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveErr(null); setSaved(false);

    if (form.password.length < 8) {
      setSaveErr('Password must be at least 8 characters.');
      return;
    }
    if (form.password !== form.confirm) {
      setSaveErr("Passwords don't match — double-check and try again.");
      return;
    }

    setSaving(true);
    try {
      await api.post('/auth/set-password', { password: form.password });
      setForm({ password: '', confirm: '' });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveErr(e.response?.data?.error || 'Could not update password. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection title="Password" subtitle="Update the password you use to sign in.">
      <form onSubmit={handleSave} className="space-y-4">
        <Field label="New password">
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            autoComplete="new-password"
            placeholder="At least 8 characters"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </Field>
        <Field label="Confirm new password">
          <input
            type="password"
            name="confirm"
            value={form.confirm}
            onChange={handleChange}
            autoComplete="new-password"
            placeholder="Type it again"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </Field>

        {saveErr && <p className="text-sm text-red-600">{saveErr}</p>}

        <button
          type="submit"
          disabled={saving}
          className="mt-2 px-6 py-2.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
        >
          {saving ? 'Updating…' : saved ? '✓ Updated' : 'Update password'}
        </button>
      </form>
    </SettingsSection>
  );
}

// ─── Dashboard Settings ───────────────────────────────────────────────────────

function DashboardSettings() {
  const { user, updateUser } = useAuth();
  const [adhdMode, setAdhdMode] = useState(user?.adhd_mode || false);
  const [saving, setSaving]     = useState(false);

  const handleToggle = async (value) => {
    setSaving(true);
    try {
      await updateMemberPreferences({ adhd_mode: value });
      updateUser({ adhd_mode: value });
      setAdhdMode(value);
    } catch { /* silently fail */ }
    setSaving(false);
  };

  return (
    <SettingsSection
      title="Dashboard View"
      subtitle="Adjust how your daily dashboard works."
    >
      <Toggle
        label="Simplified 'What's Next' view"
        description="Shows one task at a time with a big checkbox — great for ADHD and low-energy days when a long list feels overwhelming."
        checked={adhdMode}
        onChange={saving ? () => {} : handleToggle}
      />
    </SettingsSection>
  );
}

// ─── Privacy Settings ─────────────────────────────────────────────────────────

function PrivacySettings() {
  const { user, updateUser } = useAuth();
  const [shared, setShared] = useState(user?.share_personal_data_with_mentor || false);
  const [saving, setSaving] = useState(false);

  const handleToggle = async (value) => {
    setSaving(true);
    try {
      await updateMemberPreferences({ share_personal_data_with_mentor: value });
      updateUser({ share_personal_data_with_mentor: value });
      setShared(value);
    } catch { /* silently fail */ }
    setSaving(false);
  };

  return (
    <SettingsSection
      title="Privacy"
      subtitle="Your check-ins, Compass, and Continuums are shared with Jenn by default, so she can support you directly. You can turn this off anytime."
    >
      <Toggle
        label="Share with Jenn (Chief Mentor)"
        description="Other Team365 staff never see this, whether or not you turn this on. Turn it off if you'd rather keep this data to yourself."
        checked={shared}
        onChange={saving ? () => {} : handleToggle}
      />
      {/* Policy links (Jenn, 2026-09-18). The .html pages are the real ones —
          the bare 365dayreset.ca/*.html paths redirect to the homepage. */}
      <p className="text-sm text-gray-500">
        Read our{' '}
        <a href="https://365reset.com/privacy.html" target="_blank" rel="noopener noreferrer"
           className="text-reset-terracotta underline hover:no-underline">Privacy Policy</a>
        {' '}and{' '}
        <a href="https://365reset.com/terms.html" target="_blank" rel="noopener noreferrer"
           className="text-reset-terracotta underline hover:no-underline">Terms of Service</a>.
      </p>
    </SettingsSection>
  );
}

// ─── Nudge Settings ───────────────────────────────────────────────────────────

/** Small pill toggle for a per-reminder Email/SMS choice — same visual
 * language as the dashboard-reminder time-slot pills below. */
function ChannelPill({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
        active
          ? 'bg-reset-terracotta text-white border-reset-terracotta'
          : 'border-gray-200 text-gray-600 hover:border-reset-terracotta hover:text-reset-terracotta'
      }`}
    >
      {label}
    </button>
  );
}

/** Shared bordered card shell so every reminder type looks like one
 * self-contained unit — on/off (or its own header), schedule, and
 * delivery choice all together, instead of scattered across the page. */
function ReminderCard({ children }) {
  return <div className="border border-gray-100 rounded-xl p-3 space-y-3">{children}</div>;
}

/** One reminder type: on/off, plus its own Email/SMS delivery choice.
 * Schedule controls and channel pills always show, on or off, so you can
 * set them up before switching the reminder on. */
function ReminderTypeRow({ label, description, enabled, onToggleEnabled, emailOn, smsOn, onToggleEmail, onToggleSms, children }) {
  return (
    <ReminderCard>
      <Toggle label={label} description={description} checked={enabled} onChange={onToggleEnabled} />
      {children}
      <div className="flex gap-2">
        <ChannelPill label="Email" active={emailOn} onClick={() => onToggleEmail(!emailOn)} />
        <ChannelPill label="SMS" active={smsOn} onClick={() => onToggleSms(!smsOn)} />
      </div>
    </ReminderCard>
  );
}

function NudgeSettings() {
  const { prefs, isLoading, error, save, snooze } = useNudgePrefs();
  const [form, setForm]     = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  useEffect(() => { if (prefs) setForm({ ...prefs }); }, [prefs]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSave = async () => {
    setSaving(true); setSaved(false); setSaveErr(null);
    try {
      await save(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveErr(e.response?.data?.error || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const handleSnooze = async (days) => {
    const until = days === 0 ? null : new Date(Date.now() + days * 86400000).toISOString();
    await snooze(until);
    setForm(f => ({ ...f, snooze_until: until }));
  };

  if (isLoading) return <SettingsSection title="Reminders"><Skeleton /></SettingsSection>;
  if (error)     return <SettingsSection title="Reminders"><ErrorMsg msg={error} /></SettingsSection>;
  if (!form)     return null;

  const snoozedUntil = form.snooze_until ? new Date(form.snooze_until) : null;
  const isSnoozed    = snoozedUntil && snoozedUntil > new Date();

  return (
    <SettingsSection title="Reminders" subtitle="Choose which reminders you want, and how you'd like to get each one.">

      {/* Each reminder type is one self-contained card: on/off, its own
          schedule, and its own Email/SMS delivery choice. */}
      <Field label="Reminder types">
        <div className="space-y-3">
          <ReminderTypeRow
            label="Compass check-in"
            description="A reminder to complete your Compass check-in."
            enabled={form.morning_checkin_enabled ?? true}
            onToggleEnabled={v => set('morning_checkin_enabled', v)}
            emailOn={form.morning_checkin_email ?? false}
            smsOn={form.morning_checkin_sms ?? false}
            onToggleEmail={v => set('morning_checkin_email', v)}
            onToggleSms={v => set('morning_checkin_sms', v)}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Select value={form.frequency || 'daily'} onChange={v => set('frequency', v)}>
                <option value="daily">Every day</option>
                <option value="every_other_day">Every other day</option>
                <option value="weekly">Once a week (Mondays)</option>
              </Select>
              <input
                type="time"
                value={form.preferred_time ? form.preferred_time.slice(0, 5) : '07:00'}
                onChange={e => set('preferred_time', `${e.target.value}:00`)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
              />
            </div>
          </ReminderTypeRow>

          <ReminderCard>
            <Toggle
              label="Check my Personal Dashboard"
              description="Pick as many times a day as you'd like (Eastern time)."
              checked={form.dashboard_reminder_enabled ?? true}
              onChange={v => set('dashboard_reminder_enabled', v)}
            />
            <div className="flex flex-wrap gap-2">
              {DASHBOARD_REMINDER_TIMES.map(({ field, label }) => {
                const active = form[field] ?? false;
                return (
                  <button
                    key={field}
                    type="button"
                    onClick={() => set(field, !active)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-reset-terracotta text-white border-reset-terracotta'
                        : 'border-gray-200 text-gray-600 hover:border-reset-terracotta hover:text-reset-terracotta'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <ChannelPill label="Email" active={form.dashboard_reminder_email ?? false} onClick={() => set('dashboard_reminder_email', !(form.dashboard_reminder_email ?? false))} />
              <ChannelPill label="SMS" active={form.dashboard_reminder_sms ?? false} onClick={() => set('dashboard_reminder_sms', !(form.dashboard_reminder_sms ?? false))} />
            </div>
          </ReminderCard>

          <ReminderTypeRow
            label={`Notify me if I haven't accessed My Hub in ${form.missed_checkin_days ?? 2} day${(form.missed_checkin_days ?? 2) === 1 ? '' : 's'}`}
            description="A nudge to come back to your reset if you've been away. You choose how many days, and what time it comes."
            enabled={form.missed_checkin_enabled ?? true}
            onToggleEnabled={v => set('missed_checkin_enabled', v)}
            emailOn={form.missed_checkin_email ?? false}
            smsOn={form.missed_checkin_sms ?? false}
            onToggleEmail={v => set('missed_checkin_email', v)}
            onToggleSms={v => set('missed_checkin_sms', v)}
          >
            {/* Jenn, 2026-10-26: the member chooses the number of days (was fixed at 2). */}
            <div className="flex flex-wrap items-center gap-2">
            <Select value={String(form.missed_checkin_days ?? 2)} onChange={v => set('missed_checkin_days', parseInt(v, 10))}>
              {[1, 2, 3, 4, 5, 7, 10, 14, 21, 30].map(d => <option key={d} value={d}>After {d} day{d === 1 ? '' : 's'} away</option>)}
            </Select>
            <input
              type="time"
              value={form.missed_checkin_preferred_time ? form.missed_checkin_preferred_time.slice(0, 5) : '08:00'}
              onChange={e => set('missed_checkin_preferred_time', `${e.target.value}:00`)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-reset-brown focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
            </div>
          </ReminderTypeRow>
        </div>
      </Field>

      {/* Snooze */}
      <Field label="Snooze all reminders">
        {isSnoozed ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-amber-700">
              Snoozed until {snoozedUntil.toLocaleDateString()}
            </span>
            <button
              onClick={() => handleSnooze(0)}
              className="text-xs text-reset-terracotta hover:underline"
            >
              Resume now
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {[1, 3, 7].map(d => (
              <button
                key={d}
                onClick={() => handleSnooze(d)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
              >
                {d} day{d > 1 ? 's' : ''}
              </button>
            ))}
          </div>
        )}
      </Field>

      {saveErr && <p className="text-sm text-red-600">{saveErr}</p>}

      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-2 px-6 py-2.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
      >
        {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save preferences'}
      </button>
    </SettingsSection>
  );
}

// ─── Replay Delivery Settings ─────────────────────────────────────────────────

function ReplayDeliverySettings() {
  const { user, updateUser } = useAuth();
  const [form, setForm]       = useState({
    replay_notify_email: user?.replay_notify_email || false,
    replay_notify_sms:   user?.replay_notify_sms   || false,
  });
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        replay_notify_email: user.replay_notify_email || false,
        replay_notify_sms:   user.replay_notify_sms   || false,
      });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true); setSaved(false); setSaveErr(null);
    try {
      await updateMemberPreferences(form);
      updateUser(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveErr(e.response?.data?.error || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection
      title="Hub Replay Delivery"
      subtitle="Get this week's Hub replays delivered automatically so you never miss a session."
    >
      <Toggle
        label="Send replays to my email"
        description="You'll receive a weekly email with links to all Hub replays."
        checked={form.replay_notify_email}
        onChange={v => setForm(f => ({ ...f, replay_notify_email: v }))}
      />
      <Toggle
        label="Send replays to my phone (SMS)"
        description="You'll receive a weekly text message with replay links."
        checked={form.replay_notify_sms}
        onChange={v => setForm(f => ({ ...f, replay_notify_sms: v }))}
      />

      {saveErr && <p className="text-sm text-red-600">{saveErr}</p>}

      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-2 px-6 py-2.5 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
      >
        {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save preferences'}
      </button>
    </SettingsSection>
  );
}

// ─── Send Exercises To Me ──────────────────────────────────────────────────
// Replaces the old 9-toggle "Daily Content" section (built for FEA Create/
// GHL — one boolean per fixed channel+time combo staff pre-decided).
// Jenn, 2026-09-02: real per-member choice — channel, time, and frequency,
// not a preset. "Wire up the new look... even if it isn't operational
// yet... I want them to be able to see that tonight." Preferences here are
// real and saved; nothing reads this table to actually send anything yet
// (see migration 103's own comment) — hence the In Development badge.

const ACTIVITIES = [
  { key: 'chair_yoga_am',   emoji: '🧘', label: 'Morning chair yoga' },
  { key: 'chair_yoga_pm',   emoji: '🌙', label: 'Evening chair yoga' },
  { key: 'strength_cardio', emoji: '💃', label: 'Strength & Cardio Dance Party' },
  { key: 'reiki',           emoji: '🔔', label: 'Reiki Reset' },
  { key: 'meditation',      emoji: '🌿', label: 'Mindful Minutes meditation' },
  { key: 'tai_chi',         emoji: '🚶', label: 'Tai-Chi-Inspired Walking' },
];

const FREQUENCY_OPTIONS = [
  { value: 'daily',    label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekly',   label: 'Weekly' },
];

function ActivityPrefRow({ activity, pref, onSave }) {
  const [enabled, setEnabled]   = useState(pref.enabled);
  const [channel, setChannel]   = useState(pref.channel);
  const [sendTime, setSendTime] = useState(pref.send_time?.slice(0, 5) || '08:00');
  const [frequency, setFrequency] = useState(pref.frequency);
  const [saving, setSaving]     = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const save = async (fields) => {
    setSaving(true);
    try {
      await onSave(activity.key, fields);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    save({ enabled: next, channel, sendTime, frequency });
  };

  return (
    <div className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-xl">{activity.emoji}</span>
          <p className="text-sm font-medium text-reset-brown">{activity.label}</p>
          {saving && <span className="text-[11px] text-gray-400">Saving…</span>}
          {justSaved && <span className="text-[11px] text-reset-terracotta">Saved</span>}
        </div>
        <button
          type="button" role="switch" aria-checked={enabled} onClick={toggle}
          className={`relative w-10 h-6 rounded-full transition-colors focus:outline-none ${enabled ? 'bg-reset-terracotta' : 'bg-gray-200'}`}
        >
          <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0'}`} />
        </button>
      </div>

      {enabled && (
        <div className="flex flex-wrap items-center gap-2 mt-3 pl-9">
          <div className="inline-flex bg-reset-creamDark rounded-lg p-0.5">
            {['sms', 'email'].map(c => (
              <button key={c} type="button"
                onClick={() => { setChannel(c); save({ enabled, channel: c, sendTime, frequency }); }}
                className={`text-xs font-semibold px-2.5 py-1 rounded-md ${channel === c ? 'bg-white text-reset-brown shadow-sm' : 'text-gray-500'}`}>
                {c === 'sms' ? 'SMS' : 'Email'}
              </button>
            ))}
          </div>
          <select
            value={frequency}
            onChange={e => { setFrequency(e.target.value); save({ enabled, channel, sendTime, frequency: e.target.value }); }}
            className="text-xs font-semibold bg-reset-creamDark text-reset-brown rounded-lg px-2 py-1.5 border-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          >
            {FREQUENCY_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <input
            type="time"
            value={sendTime}
            onChange={e => { setSendTime(e.target.value); save({ enabled, channel, sendTime: e.target.value, frequency }); }}
            className="text-xs font-semibold bg-reset-creamDark text-reset-brown rounded-lg px-2 py-1.5 border-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
      )}
    </div>
  );
}

function DailyContentSettings() {
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    getMyActivityPreferences().then(rows => {
      const byKey = Object.fromEntries(rows.map(r => [r.activity_key, r]));
      setPrefs(Object.fromEntries(ACTIVITIES.map(a => [
        a.key,
        byKey[a.key] || { enabled: false, channel: 'email', send_time: '08:00:00', frequency: 'daily' },
      ])));
    }).catch(() => {
      setPrefs(Object.fromEntries(ACTIVITIES.map(a => [a.key, { enabled: false, channel: 'email', send_time: '08:00:00', frequency: 'daily' }])));
    });
  }, []);

  const handleSaveOne = async (activityKey, fields) => {
    await updateMyActivityPreference(activityKey, fields);
  };

  if (!prefs) return <SettingsSection title="Send exercises to me"><Skeleton /></SettingsSection>;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold text-reset-brown">Send exercises to me</h2>
      </div>
      <p className="text-sm text-gray-500 mb-2">Turn on whichever ones fit your week, and pick your own time and how often.</p>
      <p className="text-xs text-gray-400 mb-5">
        You're always in control — flip any of these off here whenever you like. Text messages may carry your carrier's message and data rates; reply STOP to any text from us to opt out of that one, or turn it off right here. Every email includes an unsubscribe link at the bottom, or email us at team365@365reset.com and we'll take care of it.
      </p>
      <div className="space-y-4">
        {ACTIVITIES.map(activity => (
          <ActivityPrefRow key={activity.key} activity={activity} pref={prefs[activity.key]} onSave={handleSaveOne} />
        ))}
      </div>
    </div>
  );
}

// ─── Small shared components ──────────────────────────────────────────────────

function SettingsSection({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <h2 className="text-lg font-bold text-reset-brown">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500 mt-0.5 mb-5">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-reset-brown mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, children }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-reset-brown bg-white focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
    >
      {children}
    </select>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-6 rounded-full transition-colors focus:outline-none ${
          checked ? 'bg-reset-terracotta' : 'bg-gray-200'
        }`}
      >
        <span
          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
      <div>
        <p className="text-sm text-reset-brown font-medium">{label}</p>
        {description && <p className="text-xs text-gray-400">{description}</p>}
      </div>
    </label>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {[1, 2, 3].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg" />)}
    </div>
  );
}

function ErrorMsg({ msg }) {
  return (
    <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{msg}</p>
  );
}
